import React, { FC, useState } from 'react';
import { MUIFieldProps } from '../../../../../lib/types/types';
import { FileUploader } from 'react-drag-drop-files';
import {
	Backdrop,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormHelperText,
	IconButton,
	List,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { AttachFile, Delete, Download } from '@mui/icons-material';
import { get } from 'lodash';
import { isArray } from 'lodash';
import { DisplayDateTime, GetFileSize } from '../../../../../utils/formats';
import { STRAPI_URL } from '../../../../../lib/constants/url';
import { getUserJwt } from '../../../../../context/AuthHelper';
import { FILE_UPLOAD_ALLOWED_FORMATS, FILE_UPLOAD_ALLOWED_SIZE } from '../../../../../lib/constants/constant';
import { DeleteFile, PostFile } from '../../../../../lib/api/files';
import LoadingBackdrop from '../../../loading/LoadingBackdrop';

const MultiUploadField: FC<MUIFieldProps> = (props) => {
	const { label, fieldValue, updateFieldValue, isDisabled, error, errorMessage, ...restProps } = props;
	console.log(' Rest props', restProps);
	const mediaFieldKey = Object.entries(get(props, 'schema.attributes', {}))
		.filter(([_key, value]) => get(value, 'type') === 'media')
		.map(([key]) => key);

	const idPath = get(props, 'component', '') === '' ? 'id' : `${mediaFieldKey[0]}.id`;
	const urlPath = get(props, 'component', '') === '' ? 'attributes.url' : `${mediaFieldKey[0]}.attributes.url`;
	const mimePath = get(props, 'component', '') === '' ? 'attributes.mime' : `${mediaFieldKey[0]}.attributes.mime`;
	const namePath = get(props, 'component', '') === '' ? 'attributes.name' : `${mediaFieldKey[0]}.attributes.name`;
	const sizePath = get(props, 'component', '') === '' ? 'attributes.size' : `${mediaFieldKey[0]}.attributes.size`;
	const createdAtPath = get(props, 'component', '') === '' ? 'attributes.createdAt' : `${mediaFieldKey[0]}.attributes.createdAt`;

	const [imageSrc, setImageSrc] = useState<any>(null);
	const [showImage, setShowImage] = useState(false);

	const [deleteOpen, setDeleteOpen] = useState(-1);

	const [tooBigFiles, setTooBigFiles] = useState<any[]>([]);
	const [invalidFileFormat, setInvalidFileFormat] = useState<any[]>([]);
	const [tooManyFilesUploaded, setTooManyFilesUPloaded] = useState<boolean>(false);
	const [webserviceActive, setWebserviceActive] = useState<boolean>(false);

	const handleShowImagePreview = async (url: string, fileType: string) => {
		if (fileType.includes('image')) fetchFile(url);
		else; // Do Nothing
	};
	const handleDownloadFile = async (url: string, fileName: string) => {
		await fetchFile(url, true, fileName);
	};
	const fetchFile = async (url: string, download: boolean = false, fileName: string = '') => {
		try {
			setWebserviceActive(true);
			let res = await fetch(`${STRAPI_URL}${url}`, {
				headers: {
					Authorization: `Bearer ${getUserJwt()}`,
				},
			});

			const blob = await res.blob();
			const objectUrl = URL.createObjectURL(blob);
			if (download) {
				const link = document.createElement('a');
				link.href = objectUrl;
				link.download = fileName;
				document.body.appendChild(link);
				link.click();
				link.remove();
				URL.revokeObjectURL(objectUrl);
			} else {
				setImageSrc(objectUrl);
			}
		} catch (err: any) {
			console.error(' ERROR ');
		} finally {
			if (!download) setShowImage(true);
			setWebserviceActive(false);
		}
	};

	const handleUploadFiles = async (files: any) => {
		let fileArr = Array.from(files);
		let invalidFile = fileArr.filter((f: any) => !FILE_UPLOAD_ALLOWED_FORMATS.includes(f.type));
		if (invalidFile) setInvalidFileFormat(invalidFile);
		if (invalidFile.length === 0) {
			let tooBig = fileArr.filter((f: any) => f.size > FILE_UPLOAD_ALLOWED_SIZE);
			setTooBigFiles(tooBig);
		}

		let okay = fileArr.filter((f: any) => f.size <= FILE_UPLOAD_ALLOWED_SIZE && FILE_UPLOAD_ALLOWED_FORMATS.includes(f.type));
		let newFiles: any = [
			...(isArray(fieldValue) ? fieldValue : []),
			...(await Promise.all(
				okay.map(async (file) => {
					try {
						setWebserviceActive(true);
						let res = await PostFile(file);
						if (get(props, 'component', '') === '') {
							return { id: get(res[0], 'id'), attributes: res[0] };
						} else {
							return { [mediaFieldKey[0]]: { id: get(res[0], 'id'), attributes: res[0] } };
						}
					} catch (err: any) {
						console.error('ERROR: Uploading files ', err);
					} finally {
						setWebserviceActive(false);
					}
				}),
			)),
		];

		if (newFiles.length > 10) {
			setTooManyFilesUPloaded(true);
		}

		let e = { target: { value: newFiles.slice(0, 10) } };
		updateFieldValue(e as React.ChangeEvent<HTMLInputElement>);
	};

	const handleDeleteFile = async (index: number) => {
		let fileId = get(fieldValue, `${index}.${idPath}`, '');
		if (fileId) {
			setWebserviceActive(true);
			DeleteFile(fileId).finally(() => setWebserviceActive(false));
		}
		let files = fieldValue;
		let e = { target: { value: files.filter((_f: any, i: number) => i !== index) } };
		updateFieldValue(e as React.ChangeEvent<HTMLInputElement>);
	};

	return (
		<Box sx={{ mt: '1.5rem', border: '2px solid #e4e4e4', padding: '1rem' }}>
			{get(props, 'hideLabel') ? <></> : <Typography sx={{ mt: '1.5rem' }}>{label}</Typography>}
			<Backdrop open={showImage} onClick={() => setShowImage(false)} sx={{ zIndex: 999 }}>
				<img src={`${imageSrc}`} alt="Test" width="600" />
			</Backdrop>

			<LoadingBackdrop open={webserviceActive} />

			<Dialog open={deleteOpen >= 0} onClose={() => setDeleteOpen(-1)} aria-labelledby={'delete-file-dialog'}>
				<DialogTitle id={'delete-file-dialog'}>Datei löschen</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Möchten Sie die Datei <b>{get(fieldValue, `${deleteOpen}.${namePath}`)}</b> löschen?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button variant="outlined" onClick={() => setDeleteOpen(-1)} color="primary">
						Abbrechen
					</Button>
					<Button
						variant="contained"
						onClick={() => {
							handleDeleteFile(deleteOpen);
							setDeleteOpen(-1);
						}}
						color="error"
					>
						Löschen
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={tooBigFiles.length > 0} onClose={() => setTooBigFiles([])} aria-labelledby={'too-big-file-dialog'}>
				<DialogTitle id={'too-big-file-dialog'}>{tooBigFiles.length > 1 ? 'Dateien' : 'Datei'} zu groß</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Die {tooBigFiles.length > 1 ? 'Dateien' : 'Datei'}
						{tooBigFiles.map((f) => (
							<p>{f.name}</p>
						))}
						{tooBigFiles.length > 1 ? 'sind' : 'ist'} größer als die erlaubten 5 MB und{' '}
						{tooBigFiles.length > 1 ? 'wurden' : 'wurde'} daher verworfen.
					</DialogContentText>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'center' }}>
					<Button variant="outlined" onClick={() => setTooBigFiles([])}>
						Schließen
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={invalidFileFormat.length > 0}
				onClose={() => setInvalidFileFormat([])}
				aria-labelledby={'too-big-file-dialog'}
			>
				<DialogTitle id={'too-big-file-dialog'}>
					{invalidFileFormat.length > 1 ? 'Dateien' : 'Datei'} Ungültiges Dateiformat!
				</DialogTitle>
				<DialogContent sx={{ textAlign: 'left' }}>
					<DialogContentText>
						Die {invalidFileFormat.length > 1 ? 'Dateien' : 'Datei'}
						{invalidFileFormat.map((f, i) => (
							<List key={i}>{f.name}</List>
						))}
						{invalidFileFormat.length > 1 ? 'sind' : 'ist'} nicht erlaubt und{' '}
						{invalidFileFormat.length > 1 ? 'wurden' : 'wurde'} daher verworfen.
						<br /> <br />
						Zulässige Dateiformate sind: .docx, .pdf, .xlsx, .png oder .jpeg. Ältere Formate sind wie .doc oder .xls werden
						nicht akzeptiert..
					</DialogContentText>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'center' }}>
					<Button variant="outlined" onClick={() => setInvalidFileFormat([])}>
						Schließen
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={tooManyFilesUploaded}
				onClose={() => setTooManyFilesUPloaded(false)}
				aria-labelledby={'too-big-file-dialog'}
			>
				<DialogTitle id={'too-big-file-dialog'}>Zuviele Dateien</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Sie können maximal 10 Dateien hochladen. Daher wurden einige Dateien verworfen
					</DialogContentText>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'center' }}>
					<Button variant="outlined" onClick={() => setTooManyFilesUPloaded(false)}>
						Schließen
					</Button>
				</DialogActions>
			</Dialog>

			{!isDisabled && (
				<>
					<FileUploader multiple={true} handleChange={handleUploadFiles} disabled={isDisabled}>
						<Box
							sx={{
								border: `${errorMessage ? '2px dashed #f00' : '2px dashed #00818B'}`,
								borderRadius: '9px',
								padding: '2rem',
								cursor: 'pointer',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							<AttachFile sx={{ width: '3em', height: '2em' }} />
							<Typography variant="body2" component={'div'}>
								Datei auswählen oder per Drag and Drop hier ablegen.
								<br />
								Maximal 10 dateien, Jede Datei darf maximal 5 MB groß sein
							</Typography>
						</Box>
					</FileUploader>
					{errorMessage ? (
						<FormHelperText error sx={{ marginLeft: '1rem' }}>
							{errorMessage}
						</FormHelperText>
					) : null}
				</>
			)}
			<TableContainer sx={{ overflow: 'initial' }}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Dateiname</TableCell>
							<TableCell align="right">Dateigröße</TableCell>
							{!get(props, 'hideUploadDate') && <TableCell align="right">Hochgeladen am</TableCell>}
							<TableCell align="right">Aktion</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{isArray(fieldValue)
							? fieldValue.map((eachFileAttributes: any, i: number) => {
									return (
										<TableRow key={i}>
											<TableCell
												onClick={() =>
													handleShowImagePreview(
														get(eachFileAttributes, urlPath, ''),
														get(eachFileAttributes, mimePath, ''),
													)
												}
												sx={{
													...(get(eachFileAttributes, mimePath, '').includes('image') ? { cursor: 'pointer' } : {}),
												}}
											>
												{get(eachFileAttributes, namePath, '')}
											</TableCell>
											<TableCell align="right">
												{GetFileSize(
													get(eachFileAttributes, sizePath) / (get(eachFileAttributes, idPath) ? 1 : 1000),
												)}
											</TableCell>
											{!get(props, 'hideUploadDate') && (
												<TableCell align="right">
													{DisplayDateTime(get(eachFileAttributes, createdAtPath, new Date()))}
												</TableCell>
											)}
											<TableCell align="right">
												{!isDisabled && (
													<IconButton aria-label="" onClick={() => setDeleteOpen(i)}>
														<Delete />
													</IconButton>
												)}
												{get(eachFileAttributes, urlPath, '') && (
													<IconButton
														onClick={() =>
															handleDownloadFile(
																get(eachFileAttributes, urlPath),
																get(eachFileAttributes, namePath),
															)
														}
													>
														<Download />
													</IconButton>
												)}
											</TableCell>
										</TableRow>
									);
								})
							: null}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
};

export default MultiUploadField;
